import { Logger } from './Logger';
import { logConstants } from '../utils/Constants';
import { LogonResult } from './gateway/Definitions';

export const authErrorString = {
	DSAUTH_MISSING_REQUIREMENTS: 'Did not get auth requirements',
	DSAUTH_MISSING_PROTOCOLS: 'Did not get auth protocols',
	DSAUTH_PRIMARY_TOKEN_FETCH_FAIL: 'Did not get primary token',
	DSAUTH_SECONDARY_TOKEN_FETCH_FAIL: 'Did not get secondary token',
	DSAUTH_RESUME_WEBVIEW_FAIL: 'Resume web view auth failed',
	DSAUTH_PRIMARY_TOKEN_CHALLENGE_ERROR: 'Error processing primary token challenge',
	DSAUTH_REQUEST_TOKEN_RESPONSE_PARSE_ERROR: 'Error parsing request token response',
	DSAUTH_FORMS_RESPONSE_NOT_SUCCESSFUL: 'Forms AuthenticateResponse failure',
	DSAUTH_WEBVIEW_AUTH_RESPONSE_FAILURE: 'Valid Webview AuthenticateResponse not received',
	DSAUTH_NO_WEBVIEW: 'Explicit forms response does not have credential type webview',
	DSAUTH_UNKNOWN_AUTH_CHALLENGE_TYPE: 'Unknown reason in auth challenge',
	DSAUTH_AUTH_PROMPT_NOT_ALLOWED: 'Auth Prompt not allowed',
	DSAUTH_START_URL_REQUEST_EXCEPTION: 'Exception in start url request',
	DSAUTH_TOKEN_REQUEST_RESPONSE_STATUS_UNCHECKED:
		'Response for Secondary Token request is not 200/400/404',
	DSAUTH_TOKEN_REQUEST_RESPONSE_STATUS_400_OR_404:
		'Response for Secondary Token request is 400/404 - Auth domain change suspected',
	DSAUTH_TOKEN_REQUEST_RESPONSE_AUTHDOMAIN_MISMATCH:
		'Response for authenticated request is 401 with authdomain mismatch - Auth domain change suspected',
	DSAUTH_LOGIN_CANCELLED_BY_USER: 'Login cancelled by user',
	DSAUTH_SILENTLOGIN_THROUGH_OIDCREFRESH_FAILED:
		'Silent Login through OIDC Token Refresh has failed',
	DSAUTH_INVALID_PRIMARY_TOKEN_SERVICE_URL: 'Invalid Primary token service url',
	DSAUTH_REFERENCE_URI_MISSING: 'Reference uri missing in request',
	UNEXPECTED_PROXY_TRANSLATE_URL: 'Unexpected proxy during URL translation',
	STATUS_CODE_OTHER_THAN_401: 'status code is other than 401',
	GATEWAY_LOGON_FAILURE: 'Gateway logon failed',
	ROAMING_ACCOUNTS_RESPONSE_NOT_FOUND:
		'Roaming accounts response not found in GatewaySession',
	GATEWAY_SESSION_EXPIRED_ALLOWCREDUI:
		'AML needed to prompt the user for logon to complete the request but it was prevented by allowCredUi property. Set the AllowLogon + Interactive flags to allow logon with user prompts.',
};
export const authChallengeErrorString = {
	DSAUTH_UNSUPPORTED_AUTH_CHALLENGE_FORMAT: 'Unsupported auth challenge format',
	DSAUTH_AUTH_CHALLENGE_PARSE_ERROR: 'Error parsing auth challenge',
};

export const authChoiceErrorString = {
	DSAUTH_CHOICE_PROTOCOL_NOT_SUPPORTED: 'Choice protocol not supported',
	DSAUTH_REQUEST_TOKEN_CHOICES_PARSE_ERROR: 'Error parsing request token choices',
	DSAUTH_CHOICE_LOCATION_EMPTY: 'Auth Choice location empty',
	DSAUTH_CHOICE_PROTOCOL_EMPTY: 'Auth Choice protocol empty',
};

export const ParserErrorStrings = {
	GATEWAY_SESSION_PARSE_ERROR: 'Error parsing gateway session response document',
	GATEWAY_POSTBACK_RESPONSE_PARSE_ERROR: 'Error parsing gateway postback response',
	GATEWAY_POSTBACK_RESPONSE_NOT_SUCCESSFUL: 'Gateway postback response is not successful',
};

export const AMErrorCode = {
	/// <summary>
	/// allowCredUi is set to false and the auth prompt is disabled
	/// </summary>
	AM_AUTH_RESULT_INTERACTION_NOT_ALLOWED: 6,
	/// <summary>
	/// Authentication canceled by user
	/// </summary>
	AM_AUTH_RESULT_CANCELLED_BY_USER: 8,
};

export const AMLErrorCode = {
	/// <summary>
	/// Default error code returned by AuthmanLite in case of unknown exceptions
	/// </summary>
	AML_START_URL_REQUEST_EXCEPTION: 40,
	/// <summary>
	/// Error parsing the response of requesting a token
	/// </summary>
	AML_REQUEST_TOKEN_RESPONSE_PARSE_ERROR: 41,
	/// <summary>
	/// Fetching of secondary token failed
	/// </summary>
	AML_SECONDARY_TOKEN_FETCH_FAIL: 42,
	/// <summary>
	/// Fetching of primary token failed
	/// </summary>
	AML_PRIMARY_TOKEN_FETCH_FAIL: 43,
	/// <summary>
	/// AuthmanLite received an invalid JSON string of request parameters from the client
	/// If this error code is seen, it means there's an issue in the way Native is constructing
	/// the JSON string to send to AuthmanLite.
	/// </summary>
	AML_INVALID_INPUT: 44,
	/// <summary>
	/// Corner case: Insufficient storage space on disk, resulting in an inability to store
	/// cookies and tokens in indexeddb
	/// </summary>
	AML_INSUFFICIENT_STORAGE_SPACE: 45,
	/// <summary>
	/// Suspected Auth Domain change (i.e HTTP request for secondary token returned 400 or 404)
	/// </summary>
	AML_POSSIBLE_AUTH_DOMAIN_CHANGE: 46,
	/// service url of Primary Token doesn't match with location given in secondary token challenge
	/// </summary>
	AML_INVALID_PRIMARY_TOKEN_SERVICE_URL: 47,
	/// <summary>
	/// Reference Uri missing in request
	/// </summary>
	AML_REQUEST_MISSING_REFERENCE_URI: 48,
	/// <summary>
	/// Failure in refreshing token when allowCredUi is false but allowSilentLogin is true
	/// </summary>
	AML_SILENT_LOGIN_FAILURE: 49,
	/// <summary>
	/// Request native to restart webview and load aml again
	/// </summary>
	AML_REQUEST_WEBVIEW_RESTART: 50,
	/// <summary>
	/// Error while initializing aml
	/// </summary>
	AML_INIT_GENERAL_FAILURE: 51,
};

export enum AuthStorageErrorCodes {
	/// <summary>
	/// Failure in opening aml database
	/// </summary>
	AML_DB_OPEN_FAILURE = 1,
	/// <summary>
	/// Failure in opening aml database and DB deletion successfull
	/// </summary>
	AML_DB_OPEN_FAILURE_DELETED_DB_SUCCESSFULLY,
	/// <summary>
	/// Failure in opening aml database and error is unknown
	/// </summary>
	AML_DB_INIT_UNKNOWN_ERROR,
}
export class RequestTokenResponseParseError extends Error {
	constructor(private innerError: Error) {
		super();
		Logger.getInstance().write(logConstants.ERROR, this.innerError.stack);
		this.message = authErrorString.DSAUTH_REQUEST_TOKEN_RESPONSE_PARSE_ERROR;
	}
}

export class RequestTokenChoicesParseError extends Error {
	constructor(private innerError: Error) {
		super();
		Logger.getInstance().write(logConstants.ERROR, this.innerError.stack);
		this.message = authChoiceErrorString.DSAUTH_REQUEST_TOKEN_CHOICES_PARSE_ERROR;
	}
}

export class PrimaryTokenFetchFailure extends Error {
	constructor(public innerError: Error) {
		super();
		Logger.getInstance().write(logConstants.ERROR, this.innerError.stack);
		this.message = authErrorString.DSAUTH_PRIMARY_TOKEN_FETCH_FAIL;
	}
}

export class SecondaryTokenFetchFailure extends Error {
	constructor(private innerError: Error) {
		super();
		Logger.getInstance().write(logConstants.ERROR, this.innerError.stack);
		this.message = authErrorString.DSAUTH_SECONDARY_TOKEN_FETCH_FAIL;
	}
}

export class FormsAuthResponseParseError extends Error {
	constructor(message: string) {
		super();
		this.message = message;
	}
}

export class WebviewAuthResponseParseError extends Error {
	constructor(message: string) {
		super();
		this.message = message;
	}
}

export class GatewayPostbackAuthResponseParserError extends Error {
	constructor(message: string) {
		super();
		this.message = message;
	}
}

export class GatewaySessionError extends Error {
	constructor(message: LogonResult) {
		super();
		this.message = `${
			authErrorString.GATEWAY_LOGON_FAILURE
		} with error code ${message.toString()}`;
	}
}

export class GatewayAuthError extends Error {
	constructor(message: string) {
		super();
		this.message = message;
	}
}

export class UnsupportedCredentialTypeError extends Error {
	constructor(message: string) {
		super();
		this.message = message;
	}
}

export class UnsupportedAuthChoiceProtocolError extends Error {
	constructor(message: string) {
		super();
		this.message = message;
	}
}

export class MissingAuthProtocolsError extends Error {
	constructor(message: string) {
		super();
		this.message = message;
	}
}

export class MissingAuthRequirementsError extends Error {
	constructor(message: string) {
		super();
		this.message = message;
	}
}

export class AuthStorageInitError extends Error {
	errorCode: number;
	constructor(errorCode: number, message: string) {
		super();
		this.message = message;
		this.errorCode = errorCode;
	}
}

export class AuthWebviewHandlerError extends Error {
	constructor(message: string) {
		super();
		this.message = message;
	}
}
